import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import WarpDrive from "./WarpDrive";
import FloatingMusicPlayer from "./FloatingMusicPlayer";

// ─── Data ─────────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { name: "Home",    href: "#home" },
  { name: "About",   href: "#about" },
  { name: "Blog",    href: "/blog" },
  { name: "Contact", href: "#contact" },
];

const MORE_ITEMS = [
  {
    name: "Projects", href: "#projects",
    icon: <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M7 5V4a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M2 9h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  },
  {
    name: "Education", href: "#education",
    icon: <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M10 3L2 7l8 4 8-4-8-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M5 9v4c0 1.7 2.2 3 5 3s5-1.3 5-3V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  },
  {
    name: "Skills", href: "#skills",
    icon: <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M10 2l1.8 5.5H18l-4.9 3.6 1.9 5.5L10 13l-5 3.6 1.9-5.5L2 7.5h6.2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  },
  {
    name: "Competitive", href: "#competitive-programming",
    icon: <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M10 2v4M6 4H4v5c0 2.2 2.7 4 6 4s6-1.8 6-4V4h-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M7 17h6M10 13v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  },
];

// ─── SVG Real Egg ─────────────────────────────────────────────────────────────

const RealEgg = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", filter: "drop-shadow(0 3px 12px rgba(0,0,0,0.55))" }}>
    <defs>
      <radialGradient id="egg-shell" cx="38%" cy="28%" r="62%" fx="38%" fy="28%">
        <stop offset="0%"   stopColor="#fffef8"/>
        <stop offset="35%"  stopColor="#f5f0e4"/>
        <stop offset="75%"  stopColor="#e8dfc8"/>
        <stop offset="100%" stopColor="#c8b89a"/>
      </radialGradient>
      <radialGradient id="egg-shadow" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="transparent"/>
        <stop offset="60%"  stopColor="transparent"/>
        <stop offset="100%" stopColor="rgba(0,0,0,0.18)"/>
      </radialGradient>
      <radialGradient id="egg-shine" cx="35%" cy="22%" r="28%">
        <stop offset="0%"   stopColor="rgba(255,255,255,0.90)"/>
        <stop offset="50%"  stopColor="rgba(255,255,255,0.30)"/>
        <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
      </radialGradient>
      <radialGradient id="egg-shine2" cx="70%" cy="72%" r="18%">
        <stop offset="0%"   stopColor="rgba(255,255,255,0.35)"/>
        <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
      </radialGradient>
    </defs>
    <path d="M30 4 C 14 4, 4 22, 4 40 C 4 56, 15 68, 30 68 C 45 68, 56 56, 56 40 C 56 22, 46 4, 30 4 Z" fill="url(#egg-shell)"/>
    <path d="M30 4 C 14 4, 4 22, 4 40 C 4 56, 15 68, 30 68 C 45 68, 56 56, 56 40 C 56 22, 46 4, 30 4 Z" fill="url(#egg-shadow)"/>
    <ellipse cx="22" cy="17" rx="9" ry="7" fill="url(#egg-shine)" transform="rotate(-20 22 17)"/>
    <ellipse cx="41" cy="52" rx="5" ry="3.5" fill="url(#egg-shine2)" transform="rotate(15 41 52)"/>
    <path d="M16 60 Q30 68 44 60" stroke="rgba(180,140,100,0.20)" strokeWidth="6" strokeLinecap="round" fill="none"/>
  </svg>
);

// ─── Toggle icons ─────────────────────────────────────────────────────────────

const PersonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="22" r="10" stroke="currentColor" strokeWidth="5.5"/>
    <path d="M8 58c0-12 10.7-21 24-21s24 9 24 21" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round"/>
  </svg>
);
const RobotIcon = () => (
  <svg width="14" height="14" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="5" r="3.5" fill="currentColor"/>
    <line x1="32" y1="8.5" x2="32" y2="15" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round"/>
    <rect x="8" y="15" width="48" height="38" rx="9" stroke="currentColor" strokeWidth="5.5"/>
    <circle cx="23" cy="32" r="5.5" fill="currentColor"/>
    <circle cx="41" cy="32" r="5.5" fill="currentColor"/>
    <path d="M22 44h20" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round"/>
  </svg>
);
const SpaceIcon = () => (
  <svg width="14" height="14" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="14" stroke="currentColor" strokeWidth="6"/>
    <ellipse cx="32" cy="32" rx="30" ry="11" stroke="currentColor" strokeWidth="4" transform="rotate(-35 32 32)"/>
  </svg>
);

// ─── Three-way toggle ─────────────────────────────────────────────────────────

function ThreeWayToggle({ options = ["Space", "Classic", "Agent"], value, defaultValue, onChange }) {
  const [internal, setInternal] = useState(defaultValue ?? options[0]);
  const current = value ?? internal;
  const btnRefs = useRef([]);
  const [plate, setPlate] = useState({ left: 2, width: 0 });

  const select = (opt) => { if (value === undefined) setInternal(opt); onChange?.(opt); };

  useEffect(() => {
    const idx = Math.max(0, options.indexOf(current));
    const btn = btnRefs.current[idx];
    if (!btn) return;
    setPlate({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [current, options]);

  return (
    <div className="nb-toggle">
      <motion.div
        animate={{ left: plate.left, width: plate.width }}
        initial={false}
        transition={{ type: "spring", stiffness: 600, damping: 40, mass: 0.45 }}
        style={{
          position: "absolute", top: 2, height: "calc(100% - 4px)",
          borderRadius: "9999px",
          background: "linear-gradient(145deg, rgba(255,255,255,0.97) 0%, rgba(220,222,228,0.94) 100%)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.40), 0 0 0 0.5px rgba(255,255,255,0.22) inset, 0 1px 0 rgba(255,255,255,0.80) inset",
          pointerEvents: "none", zIndex: 5,
        }}
      />
      {options.map((opt, i) => {
        const active = current === opt;
        return (
          <button key={opt} ref={el => btnRefs.current[i] = el} onClick={() => select(opt)}
            className="nb-toggle-btn"
            style={{ color: active ? "#0f0f10" : "rgba(255,255,255,0.30)" }}
            aria-pressed={active} aria-label={opt} title={opt}
          >
            {opt === "Agent" ? <RobotIcon /> : opt === "Classic" ? <PersonIcon /> : <SpaceIcon />}
          </button>
        );
      })}
    </div>
  );
}

// ─── Arrow helper ─────────────────────────────────────────────────────────────

const ChevronRight = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const STYLES = `
  .nb-svg-filters { position: absolute; width: 0; height: 0; overflow: hidden; pointer-events: none; }

  :root {
    --nb-h:    58px;
    --nb-r:    9999px;
    --nb-font: 'DM Sans', -apple-system, sans-serif;
    --nb-c0:   rgba(255,255,255,0.58);
    --nb-c1:   rgba(255,255,255,0.96);
  }

  @keyframes nb-in { from { opacity:0; } to { opacity:1; } }

  .nb-shell {
    font-family: var(--nb-font);
    position: relative; border-radius: var(--nb-r); overflow: hidden; height: var(--nb-h);
    animation: nb-in 0.15s ease-out both; transform: translateZ(0); will-change: transform;
    contain: layout style; border: 1px solid rgba(255,255,255,0.22);
    box-shadow:
      0 24px 72px rgba(0,0,0,0.70), 0  8px 28px rgba(0,0,0,0.38),
      0  1px  0   rgba(255,255,255,0.32) inset, 1px 0   0   rgba(255,255,255,0.08) inset,
      0 -1px  0   rgba(0,0,0,0.35) inset, 0  0    0  1.5px rgba(0,0,0,0.45);
  }
  @media (max-width:767px) { .nb-shell { border-radius: 22px; height: auto; } }

  .nb-glass-base {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 1;
    background: rgba(9, 9, 12, 0.76); transition: background 0.3s ease;
  }
  .nb-glass-base.scrolled { background: rgba(5, 5, 8, 0.90); }

  .nb-glass-fx {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 2;
    backdrop-filter: blur(28px) saturate(175%) brightness(1.04);
    -webkit-backdrop-filter: blur(28px) saturate(175%) brightness(1.04);
    background: linear-gradient(128deg, rgba(255,255,255,0.072) 0%, rgba(255,255,255,0.012) 42%, rgba(160,180,255,0.022) 75%, rgba(0,0,0,0.055) 100%);
    will-change: backdrop-filter; transform: translateZ(0); contain: strict;
  }
  .nb-glass-fx::before {
    content: ''; position: absolute; top: 0; left: 6%; right: 6%; height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 8%, rgba(255,255,255,0.38) 28%, rgba(255,255,255,0.38) 72%, rgba(255,255,255,0.08) 92%, transparent 100%);
    border-radius: 9999px;
  }
  .nb-glass-fx::after {
    content: ''; position: absolute; inset: 0; border-radius: inherit;
    background: linear-gradient(108deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.000) 40%, rgba(255,255,255,0.000) 60%, rgba(0,0,0,0.035) 100%);
    pointer-events: none;
  }

  .nb-row {
    position: relative; z-index: 3; display: flex; align-items: center;
    height: var(--nb-h); padding: 0 10px 0 11px;
  }

  .nb-logo {
    display: inline-flex; align-items: center; cursor: pointer; flex-shrink: 0; position: relative;
    user-select: none; -webkit-user-select: none; perspective: 1400px; margin-right: 2px;
    transform: translateZ(0); will-change: transform;
  }
  .nb-logo-img {
    height: 3.0rem; width: auto; display: block;
    filter: drop-shadow(0 1px 8px rgba(0,0,0,0.50));
    transition: transform 0.36s cubic-bezier(0.34,1.56,0.64,1), opacity 0.36s cubic-bezier(0.34,1.56,0.64,1);
    backface-visibility: hidden; will-change: transform, opacity; transform: translateZ(0);
  }
  .nb-logo-egg-wrap {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    backface-visibility: hidden; transform: rotateY(-180deg) scale(0.5) translateZ(0); opacity: 0;
    transition: transform 0.40s cubic-bezier(0.34,1.56,0.64,1), opacity 0.36s;
    pointer-events: none; will-change: transform, opacity;
  }
  .nb-logo.egg-visible .nb-logo-img      { transform: rotateY(180deg)  scale(0.5) translateZ(0); opacity: 0; }
  .nb-logo.egg-visible .nb-logo-egg-wrap { transform: rotateY(0deg)    scale(1)   translateZ(0); opacity: 1; }
  .nb-logo-glow {
    position: absolute; inset: -16px; border-radius: 50%; pointer-events: none;
    background: radial-gradient(circle, rgba(255,255,255,0.09) 0%, transparent 70%);
    filter: blur(14px); opacity: 0; transition: opacity 0.12s ease; will-change: opacity;
  }
  .nb-logo:hover .nb-logo-glow { opacity: 1; }

  .nb-nav {
    position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    display: flex !important; align-items: center; pointer-events: auto; z-index: 4;
  }
  @media (max-width: 767px) { .nb-nav { display: none !important; } }

  .nb-link {
    position: relative; display: inline-flex; align-items: center;
    padding: 7px 13px; border-radius: var(--nb-r);
    font-size: 13.5px; font-weight: 400; letter-spacing: 0.012em; color: var(--nb-c0);
    text-decoration: none; font-family: var(--nb-font); cursor: pointer; white-space: nowrap;
    flex-shrink: 0; -webkit-tap-highlight-color: transparent; overflow: hidden;
    transition: color 60ms linear; --mx: 50%; --my: 50%; transform: translateZ(0); will-change: transform;
  }
  .nb-link::after {
    content: ''; position: absolute; inset: 0; border-radius: var(--nb-r);
    background: rgba(255,255,255,0.082); border: 1px solid rgba(255,255,255,0.10);
    opacity: 0; transition: opacity 0ms linear; pointer-events: none;
  }
  .nb-link::before {
    content: ''; position: absolute; left: var(--mx); top: var(--my); width: 80px; height: 80px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 68%);
    border-radius: 50%; opacity: 0; transition: opacity 0ms linear; pointer-events: none; will-change: opacity;
  }
  .nb-link:hover { color: var(--nb-c1); }
  .nb-link:hover::before { opacity: 1; transition: opacity 70ms linear; }
  .nb-link:hover::after  { opacity: 1; transition: opacity 70ms linear; }
  .nb-link.active { color: rgba(255,255,255,0.90); font-weight: 500; }
  .nb-link.active .nb-link-dot {
    position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%);
    width: 3px; height: 3px; border-radius: 9999px; background: rgba(255,255,255,0.55); display: block;
  }

  .nb-more {
    position: relative; display: inline-flex; align-items: center; gap: 4px;
    padding: 7px 12px; border-radius: var(--nb-r); font-size: 13.5px; font-weight: 400;
    color: rgba(255,255,255,0.36); font-family: var(--nb-font); cursor: pointer;
    transition: color 60ms linear; flex-shrink: 0; overflow: hidden;
    --mx: 50%; --my: 50%; transform: translateZ(0); will-change: transform;
  }
  .nb-more::after {
    content: ''; position: absolute; inset: 0; border-radius: var(--nb-r);
    background: rgba(255,255,255,0.082); border: 1px solid rgba(255,255,255,0.10);
    opacity: 0; transition: opacity 0ms linear; pointer-events: none;
  }
  .nb-more::before {
    content: ''; position: absolute; left: var(--mx); top: var(--my); width: 80px; height: 80px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255,255,255,0.20) 0%, transparent 68%);
    border-radius: 50%; opacity: 0; transition: opacity 0ms linear;
    pointer-events: none; will-change: opacity; z-index: 1;
  }
  .nb-more:hover { color: rgba(255,255,255,0.82); }
  .nb-more:hover::before { opacity: 1; transition: opacity 70ms linear; }
  .nb-more:hover::after  { opacity: 1; transition: opacity 70ms linear; }
  .nb-more.open { color: rgba(255,255,255,0.97); }
  .nb-more.open::after { opacity: 1; background: rgba(255,255,255,0.096); border-color: rgba(255,255,255,0.13); }
  .nb-more-chev { display: block; position: relative; z-index: 2; transition: transform 0.16s cubic-bezier(0.16,1,0.3,1); }
  .nb-more.open .nb-more-chev { transform: rotate(180deg); }
  .nb-more-txt  { position: relative; z-index: 2; }

  .nb-spacer { flex: 1; min-width: 4px; }
  .nb-right  { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

  .nb-toggle {
    position: relative; display: inline-flex; align-items: center;
    height: 32px; border-radius: var(--nb-r); padding: 2px; flex-shrink: 0; gap: 1px;
    background: rgba(0,0,0,0.52); border: 1px solid rgba(255,255,255,0.10);
    box-shadow: 0 3px 12px rgba(0,0,0,0.55) inset, 0 1px 0 rgba(255,255,255,0.07) inset, 0 0 0 1px rgba(0,0,0,0.40);
  }
  .nb-toggle-btn {
    position: relative; z-index: 10; width: 28px; height: 28px;
    display: flex; align-items: center; justify-content: center;
    border-radius: var(--nb-r); cursor: pointer; border: none; background: transparent;
    font-family: inherit; transition: color 50ms linear; flex-shrink: 0;
  }
  .nb-toggle-btn:active { transform: scale(0.86); }

  .nb-drop {
    position: fixed; width: 210px; border-radius: 18px; border: 1px solid rgba(255,255,255,0.22);
    overflow: hidden; z-index: 9998; transform: translateZ(0);
    box-shadow:
      0 24px 72px rgba(0,0,0,0.70), 0  8px 28px rgba(0,0,0,0.38),
      0  1px  0   rgba(255,255,255,0.32) inset, 1px 0   0   rgba(255,255,255,0.08) inset,
      0 -1px  0   rgba(0,0,0,0.35) inset, 0  0    0  1.5px rgba(0,0,0,0.45);
    font-family: var(--nb-font);
  }
  .nb-drop-base { position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 0; background: rgba(9, 9, 12, 0.76); }
  .nb-drop-blur {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 1;
    backdrop-filter: blur(28px) saturate(175%) brightness(1.04);
    -webkit-backdrop-filter: blur(28px) saturate(175%) brightness(1.04);
    background: linear-gradient(128deg, rgba(255,255,255,0.072) 0%, rgba(255,255,255,0.012) 42%, rgba(160,180,255,0.022) 75%, rgba(0,0,0,0.055) 100%);
    will-change: backdrop-filter; transform: translateZ(0);
  }
  .nb-drop-blur::before {
    content: ''; position: absolute; top: 0; left: 6%; right: 6%; height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 8%, rgba(255,255,255,0.38) 28%, rgba(255,255,255,0.38) 72%, rgba(255,255,255,0.08) 92%, transparent 100%);
    border-radius: 9999px;
  }
  .nb-drop-blur::after {
    content: ''; position: absolute; inset: 0; border-radius: inherit;
    background: linear-gradient(108deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.000) 40%, rgba(255,255,255,0.000) 60%, rgba(0,0,0,0.035) 100%);
    pointer-events: none;
  }
  .nb-drop-item {
    position: relative; z-index: 2; display: flex; align-items: center; gap: 11px;
    padding: 11px 14px; font-size: 13px; font-weight: 400; color: var(--nb-c0); text-decoration: none;
    border-bottom: 1px solid rgba(255,255,255,0.08); cursor: pointer;
    -webkit-tap-highlight-color: transparent; transition: color 60ms linear;
    overflow: hidden; --mx: 50%; --my: 50%; transform: translateZ(0); will-change: transform;
  }
  .nb-drop-item::after {
    content: ''; position: absolute; inset: 0; background: rgba(255,255,255,0.07);
    opacity: 0; transition: opacity 0ms linear; pointer-events: none;
  }
  .nb-drop-item::before {
    content: ''; position: absolute; left: var(--mx); top: var(--my); width: 120px; height: 120px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 65%);
    border-radius: 50%; opacity: 0; transition: opacity 0ms linear; pointer-events: none; will-change: opacity;
  }
  .nb-drop-item:hover { color: rgba(255,255,255,0.97); }
  .nb-drop-item:hover::before { opacity: 1; transition: opacity 60ms linear; }
  .nb-drop-item:hover::after  { opacity: 1; transition: opacity 60ms linear; }
  .nb-drop-item:last-child { border-bottom: none; }
  .nb-drop-icon {
    width: 27px; height: 27px; border-radius: 7px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.42); transition: opacity 50ms linear;
  }
  .nb-drop-item:hover .nb-drop-icon { opacity: 0.9; filter: brightness(1.5); }
  .nb-drop-arrow { margin-left: auto; color: rgba(255,255,255,0.25); transition: transform 0.12s cubic-bezier(0.16,1,0.3,1); }
  .nb-drop-item:hover .nb-drop-arrow { transform: translateX(2px); }

  /* ── Mobile hamburger button ── */
  .nb-burger { display: none; }
  @media (max-width:767px) {
    .nb-burger {
      display: flex; align-items: center; justify-content: center;
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.11);
      cursor: pointer; -webkit-tap-highlight-color: transparent;
      transition: background 0.15s ease; color: rgba(255,255,255,0.70);
    }
    .nb-burger:active { background: rgba(255,255,255,0.13); }
  }

  /* ── Mobile drawer ── */
  .nb-mobile-strip { display: none; }
  @media (max-width:767px) {
    .nb-mobile-strip { display: block; position: relative; z-index: 3; overflow: hidden; }
  }
  .nb-mobile-drawer { padding: 4px 8px 12px; display: flex; flex-direction: column; gap: 2px; }
  .nb-mobile-divider {
    height: 1px; margin: 4px 4px 6px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.10) 20%, rgba(255,255,255,0.10) 80%, transparent);
  }
  .nb-mobile-section-label {
    padding: 4px 14px 2px; font-size: 10px; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(255,255,255,0.22); font-family: var(--nb-font); user-select: none;
  }
  .nb-mobile-item {
    display: flex; align-items: center; gap: 11px; padding: 11px 13px; border-radius: 12px;
    font-size: 14px; font-weight: 400; font-family: var(--nb-font);
    color: rgba(255,255,255,0.65); text-decoration: none; border: 1px solid transparent;
    transition: color 60ms linear, background 80ms linear, border-color 80ms linear;
    -webkit-tap-highlight-color: transparent; cursor: pointer;
  }
  .nb-mobile-item:active, .nb-mobile-item:hover {
    color: rgba(255,255,255,0.95); background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.10);
  }
  .nb-mobile-item-icon {
    width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.42);
    transition: color 60ms linear, background 60ms linear;
  }
  .nb-mobile-item:hover .nb-mobile-item-icon,
  .nb-mobile-item:active .nb-mobile-item-icon { color: rgba(255,255,255,0.80); background: rgba(255,255,255,0.11); }
  .nb-mobile-item-label { flex: 1; }
  .nb-mobile-item-arrow {
    color: rgba(255,255,255,0.18);
    transition: transform 0.12s cubic-bezier(0.16,1,0.3,1), color 60ms linear;
  }
  .nb-mobile-item:hover .nb-mobile-item-arrow,
  .nb-mobile-item:active .nb-mobile-item-arrow { color: rgba(255,255,255,0.38); transform: translateX(2px); }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function Navbar({ viewMode, onViewChange }) {
  const [moreOpen,  setMoreOpen]  = useState(false);
  const [gameOpen,  setGameOpen]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [dropPos,   setDropPos]   = useState({ top: 70, left: 0 });
  const [activeNav, setActiveNav] = useState(null);

  const glassBaseRef  = useRef(null);
  const moreBtnRef    = useRef(null);
  const logoRef       = useRef(null);
  const eggTimer      = useRef(null);
  const isTouchDev    = useRef(false);
  const scrollYRef    = useRef(0);
  const rafPending    = useRef(false);
  const navRafPending = useRef(false);
  const isScrolled    = useRef(false);

  useEffect(() => { isTouchDev.current = window.matchMedia("(hover: none)").matches; }, []);

  useEffect(() => {
    const show = () => logoRef.current?.classList.add("egg-visible");
    const hide = () => logoRef.current?.classList.remove("egg-visible");
    const ids = [
      setTimeout(show, 500),  setTimeout(hide, 1400),
      setTimeout(show, 4000), setTimeout(hide, 4900),
    ];
    return () => ids.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const fn = () => {
      scrollYRef.current = window.scrollY;
      if (rafPending.current) return;
      rafPending.current = true;
      requestAnimationFrame(() => {
        const y = scrollYRef.current;
        if (!isScrolled.current && y > 60)  { isScrolled.current = true;  glassBaseRef.current?.classList.add("scrolled"); }
        if ( isScrolled.current && y < 40)  { isScrolled.current = false; glassBaseRef.current?.classList.remove("scrolled"); }
        rafPending.current = false;
      });
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const allItems = [...NAV_ITEMS, ...MORE_ITEMS].filter(item => item.href.startsWith("#"));
    const compute = () => {
      const scrollY = window.scrollY + 80;
      let current = null;
      for (const item of allItems) {
        const el = document.querySelector(item.href);
        if (el && el.offsetTop <= scrollY) current = item.href.slice(1);
      }
      setActiveNav(current);
      navRafPending.current = false;
    };
    const fn = () => {
      if (navRafPending.current) return;
      navRafPending.current = true;
      requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") { setMoreOpen(false); setGameOpen(false); setMenuOpen(false); }
    };
    window.addEventListener("keydown", fn, { passive: true });
    return () => window.removeEventListener("keydown", fn);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const fn = (e) => {
      if (moreBtnRef.current?.contains(e.target)) return;
      if (e.target.closest(".nb-drop")) return;
      setMoreOpen(false);
    };
    document.addEventListener("mousedown",  fn);
    document.addEventListener("touchstart", fn);
    return () => {
      document.removeEventListener("mousedown",  fn);
      document.removeEventListener("touchstart", fn);
    };
  }, [moreOpen]);

  const showEgg = useCallback(() => {
    clearTimeout(eggTimer.current);
    logoRef.current?.classList.add("egg-visible");
  }, []);
  const hideEgg = useCallback(() => {
    logoRef.current?.classList.remove("egg-visible");
  }, []);
  const handleLogoClick = useCallback(() => {
    if (isTouchDev.current) { showEgg(); eggTimer.current = setTimeout(hideEgg, 900); }
    else setGameOpen(true);
  }, [showEgg, hideEgg]);

  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "instant", block: "start" });

  const openMore = () => {
    const r = moreBtnRef.current?.getBoundingClientRect();
    if (r) setDropPos({ top: r.bottom + 9, left: r.left + r.width / 2 - 100 });
    setMoreOpen(p => !p);
  };

  const handleViewChange = useCallback((opt) => {
    onViewChange(opt);
    if (opt === "Classic") {
      document.body.style.opacity = "0";
      document.body.style.transition = "opacity 0.28s ease-out";
      setTimeout(() => { window.location.href = "/classic/"; }, 280);
    }
  }, [onViewChange]);

  const handleMouseMove = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  }, []);

  const handleNavClick = useCallback((e, item) => {
    e.preventDefault();
    if (item.href.startsWith("/")) {
      window.location.href = item.href;
    } else {
      scrollTo(item.href);
    }
  }, []);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap"
        media="print"
        onLoad="this.media='all'"
      />
      <style>{STYLES}</style>

      <svg className="nb-svg-filters" aria-hidden="true" focusable="false">
        <defs>
          <filter id="nb-liquid-distort" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.012 0.008" numOctaves="3" seed="7" result="noise"/>
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="3.5" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
          <filter id="nb-liquid-distort2" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.014 0.010" numOctaves="3" seed="11" result="noise"/>
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="4.0" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>
      </svg>

     <div style={{
        position: "fixed", inset: "0 0 auto 0", zIndex: 50,
        display: "grid", gridTemplateColumns: "1fr minmax(0, 880px) 1fr",
        alignItems: "flex-start", gap: "12px",
        padding: "0.8rem 1rem 0", pointerEvents: "none",
      }}>
        <div style={{ justifySelf: "start", pointerEvents: "auto" }}>
          <FloatingMusicPlayer />
        </div>

        <nav className="nb-shell" style={{ width: "100%", maxWidth: "880px", pointerEvents: "auto" }}>
          <div ref={glassBaseRef} className="nb-glass-base" />
          <div className="nb-glass-fx" />
          <div className="nb-row">
            <h1 style={{ margin: 0 }}>
              <span ref={logoRef} className="nb-logo"
                onClick={handleLogoClick}
                onMouseEnter={() => { if (!isTouchDev.current) showEgg(); }}
                onMouseLeave={() => { if (!isTouchDev.current) hideEgg(); }}
                title="Prepare for Launch"
              >
                <span className="nb-logo-glow" aria-hidden="true" />
                <img src="/Logo2_hq.png" alt="Aryan Dwivedi" className="nb-logo-img"
                  draggable={false} loading="eager" decoding="async" />
                <span className="nb-logo-egg-wrap" aria-label="Easter egg">
                  <RealEgg size={22} />
                </span>
              </span>
            </h1>

            {/* Desktop nav */}
            <div className="nb-nav" style={{ display: "flex" }}>
              {NAV_ITEMS.map((item) => {
                const isActive = item.href.startsWith("#") && activeNav === item.href.slice(1);
                return (
                  <motion.a key={item.name} href={item.href}
                    className={`nb-link${isActive ? " active" : ""}`}
                    onClick={(e) => handleNavClick(e, item)}
                    onMouseMove={handleMouseMove} onMouseEnter={handleMouseMove}
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0, duration: 0.12, ease: "easeOut" }}
                  >
                    {item.name}
                    {isActive && <span className="nb-link-dot" aria-hidden="true" />}
                  </motion.a>
                );
              })}
              <motion.button ref={moreBtnRef} className={`nb-more${moreOpen ? " open" : ""}`}
                onClick={openMore} onMouseMove={handleMouseMove} onMouseEnter={handleMouseMove}
                aria-label="More sections"
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0, duration: 0.12, ease: "easeOut" }}
              >
                <span className="nb-more-txt">More</span>
                <span className="nb-more-chev">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </motion.button>
            </div>

            <div className="nb-spacer" />

            <div className="nb-right">
              {/* Hamburger — mobile only */}
              <button className="nb-burger" onClick={() => setMenuOpen(p => !p)}
                aria-label="Toggle menu" aria-expanded={menuOpen}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  {menuOpen
                    ? <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                    : <path d="M2 4.5h12M2 8h12M2 11.5h12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                  }
                </svg>
              </button>

              <motion.div initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0, duration: 0.12, ease: "easeOut" }}>
                <ThreeWayToggle options={["Space", "Classic", "Agent"]} value={viewMode} onChange={handleViewChange} />
              </motion.div>
            </div>
          </div>

          {/* Mobile drawer */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div className="nb-mobile-strip"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="nb-mobile-drawer">
                  <div className="nb-mobile-divider" />
                  <div className="nb-mobile-section-label">Navigation</div>
                  {NAV_ITEMS.map((item) => {
                    const isActive = item.href.startsWith("#") && activeNav === item.href.slice(1);
                    return (
                      <a key={item.name} href={item.href} className="nb-mobile-item"
                        style={isActive ? { color: "rgba(255,255,255,0.95)", background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.09)" } : {}}
                        onClick={(e) => { handleNavClick(e, item); setMenuOpen(false); }}
                      >
                        <span className="nb-mobile-item-label">{item.name}</span>
                        <span className="nb-mobile-item-arrow"><ChevronRight /></span>
                      </a>
                    );
                  })}
                  <div className="nb-mobile-divider" />
                  <div className="nb-mobile-section-label">More</div>
                  {MORE_ITEMS.map((item) => (
                    <a key={item.name} href={item.href} className="nb-mobile-item"
                      onClick={(e) => { e.preventDefault(); setMenuOpen(false); scrollTo(item.href); }}
                    >
                    <span className="nb-mobile-item-icon">{item.icon}</span>
                      <span className="nb-mobile-item-label">{item.name}</span>
                      <span className="nb-mobile-item-arrow"><ChevronRight /></span>
                    </a>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </nav>

        <div aria-hidden="true" />
      </div>

      {/* Desktop "More" dropdown */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div className="nb-drop" style={{ top: dropPos.top, left: dropPos.left }}
            initial={{ opacity: 0, y: -10, scale: 0.93 }}
            animate={{ opacity: 1, y: 0,   scale: 1 }}
            exit={{    opacity: 0, y: -6,  scale: 0.97 }}
            transition={{ duration: 0.14, ease: [0.16,1,0.3,1] }}
          >
            <div className="nb-drop-base" />
            <div className="nb-drop-blur" />
            {MORE_ITEMS.map((item) => (
              <a key={item.name} href={item.href} className="nb-drop-item"
                onClick={(e) => { e.preventDefault(); setMoreOpen(false); scrollTo(item.href); }}
                onMouseMove={handleMouseMove} onMouseEnter={handleMouseMove}
              >
                <span className="nb-drop-icon">{item.icon}</span>
                {item.name}
                <span className="nb-drop-arrow">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {gameOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000", overflow: "hidden" }}>
          <WarpDrive onRestart={() => setGameOpen(false)} />
        </div>
      )}
    </>
  );
}
